/* Änderung Anfang - Carousel Navigation & Swipe Funktionalität
 * - Arrow Navigation (Prev/Next Buttons)
 * - Touch Swipe Support für Mobile (Navigation)
 * - Keyboard Navigation (Pfeiltasten)
 * - Bild-Zentrierung mit translateX
 * - Animation Lock gegen Race-Conditions
 * - Zoom Funktion (Doppelklick/Doppeltap)
 * - Pan/Drag im Zoom-Modus
 * Änderung Ende */

if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.mediaItems = [];
        this.currentIndex = 0;
        this.isAnimating = false;
        this.animationDuration = 400;

        // Swipe properties (Mobile Navigation)
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.minSwipeDistance = 50;

        // Zoom properties (Desktop + Mobile)
        this.isZoomed = false;
        this.isPanning = false;
        this.panStartX = 0;
        this.panStartY = 0;
        this.currentPanX = 0;
        this.currentPanY = 0;

        // Double-tap detection
        this.lastTapTime = 0;
        this.doubleTapDelay = 300;

        // Device detection
        this.isMobile = () => window.innerWidth < 750;
      }

      connectedCallback() {
        this.setupNavigation();
        this.setupKeyboard();
        this.setupZoom();
      }

      setupNavigation() {
        const prevButton = this.querySelector('.product-media-modal__nav--prev');
        const nextButton = this.querySelector('.product-media-modal__nav--next');

        if (prevButton) {
          prevButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigate(-1);
          });
          prevButton.addEventListener('pointerup', (e) => e.stopPropagation());
        }
        if (nextButton) {
          nextButton.addEventListener('click', (e) => {
            e.stopPropagation();
            this.navigate(1);
          });
          nextButton.addEventListener('pointerup', (e) => e.stopPropagation());
        }
      }

      setupKeyboard() {
        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') {
            e.preventDefault();
            this.navigate(-1);
          } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            this.navigate(1);
          } else if (e.key === 'Escape' && this.isZoomed) {
            e.preventDefault();
            this.exitZoom();
          }
        });
      }

      // ============================================
      // ZOOM: Einheitliche Logik für Desktop + Mobile
      // ============================================

      setupZoom() {
        const content = this.querySelector('.product-media-modal__content');
        const dialog = this.querySelector('.product-media-modal__dialog');
        if (!content) return;

        // Prevent modal close on content click
        content.addEventListener('pointerup', (e) => e.stopPropagation());

        // Click on background exits zoom
        if (dialog) {
          dialog.addEventListener('click', (e) => {
            if (this.isZoomed && e.target === dialog) {
              this.exitZoom();
            }
          });
        }

        // === DESKTOP: Doppelklick + Maus-Pan ===
        content.addEventListener('dblclick', (e) => {
          if (this.isMobile()) return;
          e.preventDefault();
          e.stopPropagation();
          this.toggleZoom();
        });

        content.addEventListener('mousedown', (e) => {
          if (this.isMobile()) return;
          if (this.isZoomed) {
            this.startPan(e.clientX, e.clientY);
            e.preventDefault();
          }
        });

        document.addEventListener('mousemove', (e) => {
          if (this.isMobile()) return;
          if (this.isZoomed && this.isPanning) {
            this.doPan(e.clientX, e.clientY);
          }
        });

        document.addEventListener('mouseup', () => {
          if (this.isMobile()) return;
          this.endPan();
        });

        // === MOBILE: Doppeltap + Touch-Pan + Swipe ===
        let touchMoved = false;

        content.addEventListener('touchstart', (e) => {
          if (e.touches.length !== 1) return;

          const touch = e.touches[0];
          this.touchStartX = touch.clientX;
          this.touchStartY = touch.clientY;
          touchMoved = false;

          if (this.isZoomed) {
            this.startPan(touch.clientX, touch.clientY);
          }
        }, { passive: true });

        content.addEventListener('touchmove', (e) => {
          if (e.touches.length !== 1) return;

          const touch = e.touches[0];
          const dx = touch.clientX - this.touchStartX;
          const dy = touch.clientY - this.touchStartY;

          if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
            touchMoved = true;
          }

          if (this.isZoomed && this.isPanning) {
            e.preventDefault();
            this.doPan(touch.clientX, touch.clientY);
          }
        }, { passive: false });

        content.addEventListener('touchend', (e) => {
          if (this.isPanning) {
            this.endPan();
          }

          // Swipe-Navigation (nur wenn nicht gezoomt und bewegt)
          if (!this.isZoomed && touchMoved) {
            const touch = e.changedTouches[0];
            const swipeX = touch.clientX - this.touchStartX;
            const swipeY = Math.abs(touch.clientY - this.touchStartY);

            if (Math.abs(swipeX) > this.minSwipeDistance && Math.abs(swipeX) > swipeY) {
              this.navigate(swipeX > 0 ? -1 : 1);
              return;
            }
          }

          // Doppeltap-Erkennung (nur wenn nicht bewegt)
          if (!touchMoved) {
            const now = Date.now();
            if (now - this.lastTapTime < this.doubleTapDelay) {
              e.preventDefault();
              this.toggleZoom();
              this.lastTapTime = 0;
            } else {
              this.lastTapTime = now;
            }
          }
        }, { passive: false });
      }

      toggleZoom() {
        const activeItem = this.mediaItems[this.currentIndex];
        if (!activeItem) return;

        if (this.isZoomed) {
          this.exitZoom();
        } else {
          this.enterZoom(activeItem);
        }
      }

      enterZoom(item) {
        this.isZoomed = true;
        this.classList.add('zoom-active');
        item.classList.add('zoomed');
        this.currentPanX = 0;
        this.currentPanY = 0;
      }

      exitZoom() {
        const activeItem = this.mediaItems[this.currentIndex];
        if (activeItem) {
          activeItem.classList.remove('zoomed', 'panning');
          activeItem.style.transformOrigin = '';
        }
        this.isZoomed = false;
        this.isPanning = false;
        this.classList.remove('zoom-active');
        this.currentPanX = 0;
        this.currentPanY = 0;
      }

      startPan(clientX, clientY) {
        this.isPanning = true;
        this.panStartX = clientX - this.currentPanX;
        this.panStartY = clientY - this.currentPanY;
        const activeItem = this.mediaItems[this.currentIndex];
        if (activeItem) activeItem.classList.add('panning');
      }

      doPan(clientX, clientY) {
        this.currentPanX = clientX - this.panStartX;
        this.currentPanY = clientY - this.panStartY;

        const activeItem = this.mediaItems[this.currentIndex];
        if (activeItem) {
          const img = activeItem.tagName === 'IMG' ? activeItem : activeItem.querySelector('img');
          if (img) {
            const rect = img.getBoundingClientRect();
            const originX = 50 - (this.currentPanX / rect.width) * 100;
            const originY = 50 - (this.currentPanY / rect.height) * 100;
            activeItem.style.transformOrigin = `${originX}% ${originY}%`;
          }
        }
      }

      endPan() {
        this.isPanning = false;
        const activeItem = this.mediaItems[this.currentIndex];
        if (activeItem) {
          activeItem.classList.remove('panning');
        }
      }

      // ============================================
      // MODAL & NAVIGATION
      // ============================================

      hide() {
        if (this.isZoomed) {
          this.exitZoom();
          return;
        }
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.initializeMedia();
        this.showActiveMedia();
      }

      initializeMedia() {
        this.mediaItems = Array.from(this.querySelectorAll('[data-media-id]'));
        const activeMediaId = this.openedBy.getAttribute('data-media-id');
        this.currentIndex = this.mediaItems.findIndex(
          item => item.getAttribute('data-media-id') === activeMediaId
        );
        if (this.currentIndex === -1) this.currentIndex = 0;
      }

      navigate(direction) {
        if (this.isAnimating) return;

        if (this.isZoomed) {
          this.exitZoom();
        }

        const newIndex = this.currentIndex + direction;
        if (newIndex < 0 || newIndex >= this.mediaItems.length) return;

        this.isAnimating = true;
        this.currentIndex = newIndex;
        this.updateActiveMedia();

        setTimeout(() => {
          this.isAnimating = false;
        }, this.animationDuration);
      }

      updateSlideClasses() {
        this.mediaItems.forEach((item, index) => {
          item.classList.remove('active', 'prev-slide', 'next-slide');

          if (index === this.currentIndex) {
            item.classList.add('active');
          } else if (index === this.currentIndex - 1) {
            item.classList.add('prev-slide');
          } else if (index === this.currentIndex + 1) {
            item.classList.add('next-slide');
          }
        });
      }

      updateActiveMedia() {
        this.updateSlideClasses();

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.updateSlidePosition();
          });
        });

        const activeMedia = this.mediaItems[this.currentIndex];
        if (activeMedia) {
          const template = activeMedia.querySelector('template');
          if (activeMedia.nodeName === 'DEFERRED-MEDIA' && template) {
            activeMedia.loadContent();
          }
        }

        this.updateCounter();
        this.updateNavigationState();
      }

      updateSlidePosition() {
        const content = this.querySelector('.product-media-modal__content');
        const dialog = this.querySelector('.product-media-modal__dialog');
        if (!content || !dialog || this.mediaItems.length === 0) return;

        const activeItem = this.mediaItems[this.currentIndex];
        if (!activeItem) return;

        const dialogRect = dialog.getBoundingClientRect();
        const containerCenter = dialogRect.left + (dialogRect.width / 2);

        const activeRect = activeItem.getBoundingClientRect();
        const activeCenterX = activeRect.left + (activeRect.width / 2);

        const moveAmount = containerCenter - activeCenterX;

        const currentTransform = content.style.transform;
        const currentX = currentTransform
          ? parseFloat(currentTransform.replace(/[^-\d.]/g, '')) || 0
          : 0;

        content.style.transform = `translateX(${currentX + moveAmount}px)`;
      }

      updateCounter() {
        const currentEl = this.querySelector('.product-media-modal__counter-current');
        if (currentEl) {
          currentEl.textContent = this.currentIndex + 1;
        }
      }

      updateNavigationState() {
        const prevButton = this.querySelector('.product-media-modal__nav--prev');
        const nextButton = this.querySelector('.product-media-modal__nav--next');

        if (prevButton) {
          prevButton.disabled = this.currentIndex === 0;
          prevButton.style.opacity = this.currentIndex === 0 ? '0.3' : '1';
        }
        if (nextButton) {
          nextButton.disabled = this.currentIndex === this.mediaItems.length - 1;
          nextButton.style.opacity = this.currentIndex === this.mediaItems.length - 1 ? '0.3' : '1';
        }
      }

      showActiveMedia() {
        this.updateSlideClasses();

        const activeMedia = this.mediaItems[this.currentIndex];
        const activeMediaTemplate = activeMedia ? activeMedia.querySelector('template') : null;
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia &&
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        const content = this.querySelector('.product-media-modal__content');
        if (content) {
          content.style.transition = 'none';
          this.updateSlidePosition();
          content.offsetHeight;
          content.style.transition = '';
        }

        this.updateCounter();
        this.updateNavigationState();
      }
    }
  );
}
